import { useState, useEffect, useCallback } from "react";
import { useClientIndustryApi } from "../../../hooks/useClientIndustryApi";

export const useClientIndustriesViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getClientIndustries,
    createClientIndustry: apiCreateClientIndustry,
    updateClientIndustry: apiUpdateClientIndustry,
    deleteClientIndustry,
    cacheOperations: { readClientIndustries, writeClientIndustries },
  } = useClientIndustryApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [clientIndustriesResult] = await Promise.all([
        getClientIndustries(),
      ]);

      if (!clientIndustriesResult.success) {
        throw new Error("Failed to fetch required data");
      }

      writeClientIndustries(clientIndustriesResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getClientIndustries, writeClientIndustries, loading]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateClientIndustry = useCallback(
    async (formData) => {
      try {
        const result = await apiCreateClientIndustry(formData);
        if (result.success) {
          setSuccessModal("Client Industry created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating client industry:", error);
        throw new Error(error.message);
      }
    },
    [apiCreateClientIndustry, readClientIndustries, writeClientIndustries]
  );

  const handleUpdateClientIndustry = useCallback(
    async (clientIndustryId, formData) => {
      try {
        const result = await apiUpdateClientIndustry(
          clientIndustryId,
          formData
        );
        if (result.success) {
          setSuccessModal("Client industry updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating client industry:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateClientIndustry, readClientIndustries, writeClientIndustries]
  );

  const handleDelete = useCallback(
    async (clientIndustry) => {
      try {
        const result = await deleteClientIndustry(
          clientIndustry.client_industry_id
        );
        if (result.success) {
          setSuccessModal("Client industry deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting client industry:", err);
      }
    },
    [deleteClientIndustry, readClientIndustries, writeClientIndustries]
  );

  return {
    clientIndustries: readClientIndustries(),
    loading,
    error,
    createClientIndustry: handleCreateClientIndustry,
    updateClientIndustry: handleUpdateClientIndustry,
    handleDelete,
    successModal,
    setSuccessModal,
    refetch: fetchData,
  };
};
